import { useMemo, useState } from 'react';
import { Grid, Theme, Typography } from '@mui/material';
import { PolicyGetResponse } from '@lio/api-types';
import { Dialog } from '@components/dialog';
import { PolicyTransaction } from '@models/policyTransaction';
import { getOutOfSequenceBlockingTransactions } from '@pages/PolicyDetails/dialogs/utils/getOutOfSequenceBlockingTransactions';
import { ShortDateFormat, fakeLocalMidnightFromTZMidnight, formatDateToTimezone } from '@utils/date';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { format } from 'date-fns';
import { getFormattedPolicyNumber } from '@utils/index';
import { Checkbox } from '@components/checkbox/Checkbox';
import { FormControlLabel } from '@components/formControlLabel';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';

interface SequenceEndorsementDialogProps {
  endorsementEffectiveDate: Date;
  handleClose: (confirmed: boolean) => void;
  isOpen: boolean;
  policy: PolicyGetResponse;
  policyTransactions?: PolicyTransaction[];
}

enum EndorsementFieldNames {
  EffectiveDate = 'effectiveDate',
  Description = 'description',
  ProcessedDate = 'processedDate',
  Type = 'type',
}

const endorsementColumns: GridColDef[] = [
  {
    field: EndorsementFieldNames.Type,
    headerName: 'Type',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => row.transactionType,
    renderCell: (params: GridRenderCellParams) => (
      <OverflowTooltip>{`${params.row.transactionType ?? '-'}`}</OverflowTooltip>
    ),
  },
  {
    field: EndorsementFieldNames.EffectiveDate,
    headerName: 'Effective Date',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => formatDateToTimezone(row.effectiveOn),
    renderCell: (params: GridRenderCellParams) => (
      <OverflowTooltip>{formatDateToTimezone(params.row.effectiveOn)}</OverflowTooltip>
    ),
  },
  {
    field: EndorsementFieldNames.ProcessedDate,
    headerName: 'Processed Date',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => (row.processedOn ? formatDateToTimezone(row.processedOn) : '-'),
    renderCell: (params: GridRenderCellParams) => (
      <OverflowTooltip>{params.row.processedOn ? formatDateToTimezone(params.row.processedOn) : '-'}</OverflowTooltip>
    ),
  },
  {
    field: EndorsementFieldNames.Description,
    headerName: 'Description',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => row.description,
    renderCell: (params: GridRenderCellParams) => (
      <OverflowTooltip>{`${params.row.description ?? '-'}`}</OverflowTooltip>
    ),
  },
];

export const SequenceEndorsementDialog = ({
  isOpen,
  policyTransactions,
  policy,
  handleClose,
  endorsementEffectiveDate,
}: SequenceEndorsementDialogProps) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '...';

  const data = useMemo(() => {
    return policyTransactions
      ? getOutOfSequenceBlockingTransactions({
          policyTransactions,
          effectiveOnDate: endorsementEffectiveDate,
        })
      : [];
  }, [endorsementEffectiveDate, policyTransactions]);

  const handleCancel = () => {
    setAcknowledged(false);
    handleClose(false);
  };

  const handleContinue = () => {
    setAcknowledged(false);
    handleClose(true);
  };

  return (
    <Dialog
      open={isOpen}
      size='medium'
      title={`Endorse Policy ${formattedPolicyNumber}`}
      showBorders={false}
      handleCloseDialog={handleCancel}
      primaryBtnText='Continue OOS Endorsement'
      primaryBtnAction={handleContinue}
      isPrimaryBtnDisabled={!acknowledged}
      secondaryBtnText='Cancel'
      secondaryBtnAction={handleCancel}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Grid size={{ xs: 11 }} sx={{ margin: 'auto', marginTop: '16px' }}>
          <Typography
            variant='bodySmall'
            sx={{
              color: bodyTextColor,
            }}
          >
            This policy has existing transactions with effective dates after
            <Typography
              variant='bodySmall'
              sx={{
                color: bodyTextColor,
                fontWeight: 700,
              }}
            >
              {` ${format(fakeLocalMidnightFromTZMidnight(new Date(endorsementEffectiveDate)), ShortDateFormat)} `}
            </Typography>
            which will result in the creation of an out-of-sequence endorsement. Proceeding will require resolving these
            transactions before the policy can accept new changes.
          </Typography>
        </Grid>
        <Grid size={{ xs: 11 }}>
          <Typography variant='h6' sx={{ padding: '32px 0 16px 0' }}>
            Affected Transactions
          </Typography>
          <DataGrid
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
            columns={endorsementColumns}
            rows={data ?? []}
            rowCount={data?.length}
            initialState={{ density: 'compact' }}
            columnHeaderHeight={36}
            sx={{
              ...styles.dataGrid,
              '& .MuiDataGrid-columnHeaders': {
                borderTop: `1px solid ${'common.black'}`,
                borderBottom: `1px solid ${'common.black'}`,
                borderRadius: '0',
              },
              '& .MuiDataGrid-row': {
                borderBottomWidth: '1px',
                borderColor: 'border.default',
                borderBottomStyle: 'solid',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 11 }} sx={{ marginTop: '24px' }}>
          <FormControlLabel
            label="I'm sure that I want to create an out-of-sequence endorsement."
            typographyProps={{ variant: 'bodySmall' }}
            control={
              <Checkbox
                sx={{ marginLeft: -1 }}
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
            }
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};

// black reads on LIO's white dialog but not on a dark palette's
const bodyTextColor = (theme: Theme) => (theme.palette.theme === 'lio' ? 'common.black' : 'text.main');
